import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, MapPin, Sun, Compass } from "lucide-react";
import { useLenis } from "lenis/react";
import { Reveal } from "./Reveal";
import { DESTINATIONS } from "../../data/destinations";

const DestinationCard = ({ dest, tall, offset, onOpen }) => {
  const [spot, setSpot] = useState({ x: 50, y: 50, on: false });

  return (
    <Reveal className={offset ? "lg:mt-16" : ""}>
      <button
        data-testid={`destination-card-${dest.id}`}
        onClick={() => onOpen(dest)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setSpot({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100, on: true });
        }}
        onMouseLeave={() => setSpot((s) => ({ ...s, on: false }))}
        className={`group relative block w-full overflow-hidden text-left ${
          tall ? "rounded-tr-[4rem] rounded-bl-[4rem]" : "rounded-tl-[4rem] rounded-br-[4rem]"
        }`}
      >
        <div className={`${tall ? "h-[480px] sm:h-[560px]" : "h-[400px] sm:h-[440px]"} overflow-hidden`}>
          <img
            src={dest.image}
            alt={`${dest.name}, ${dest.country}`}
            loading="lazy"
            style={dest.imagePos ? { objectPosition: dest.imagePos } : undefined}
            className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-110"
          />
        </div>
        <div
          className="pointer-events-none absolute inset-0 transition-opacity duration-500"
          style={{
            opacity: spot.on ? 1 : 0,
            background: `radial-gradient(340px at ${spot.x}% ${spot.y}%, rgba(226,114,91,0.28), transparent 65%)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D1F23]/60 via-[#0D1F23]/5 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-7 sm:p-9">
          <div>
            <h3 className="mt-2 font-display text-3xl font-light text-[#F7F0E3] sm:text-4xl">{dest.name}</h3>
            <p className="mt-2 max-w-xs text-sm text-[#F7F0E3]/70 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
              {dest.blurb}
            </p>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#F7F0E3]/30 text-[#F7F0E3] transition-colors duration-300 group-hover:border-[#E2725B] group-hover:bg-[#E2725B] group-hover:text-[#0D1F23]">
            <ArrowUpRight size={18} />
          </span>
        </div>
      </button>
    </Reveal>
  );
};

const DestinationModal = ({ dest, onClose, onEnquire }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0D1F23]/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="destination-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="grid max-h-[90vh] w-full max-w-4xl overflow-hidden overflow-y-auto rounded-3xl bg-[#F7F0E3] md:grid-cols-2"
      >
        <div className="relative h-56 md:h-auto">
          <img src={dest.image} alt={dest.name} style={dest.imagePos ? { objectPosition: dest.imagePos } : undefined} className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="relative p-8 sm:p-10">
          <button
            data-testid="destination-modal-close"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-[#0D1F23]/15 text-[#0D1F23] transition-colors hover:bg-[#0D1F23] hover:text-[#F7F0E3]"
          >
            <X size={16} />
          </button>
          <h3 className="mt-2 font-display text-4xl font-light text-[#0D1F23]">{dest.name}</h3>
          <p className="mt-4 text-base leading-relaxed text-[#4A5557]">{dest.blurb}</p>

          <div className="mt-6 space-y-3 border-t border-[#0D1F23]/10 pt-6">
            <p className="flex items-center gap-3 text-sm text-[#4A5557]">
              <Sun size={15} className="text-[#E2725B]" /> Best season: {dest.season}
            </p>
            <p className="flex items-center gap-3 text-sm text-[#4A5557]">
              <Compass size={15} className="text-[#E2725B]" /> Pace: {dest.pace}
            </p>
          </div>

          <ul className="mt-6 space-y-2.5">
            {dest.teaser.map((t) => (
              <li key={t} className="flex items-start gap-3 font-display text-lg italic text-[#13272B]">
                <span className="mt-2.5 h-1 w-4 shrink-0 rounded-full bg-[#E2725B]" />
                {t}
              </li>
            ))}
          </ul>

          {dest.properties && (
            <div className="mt-6">
              <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#4A5557]">
                Properties We Love
              </p>
              <div className="mt-3 flex flex-wrap gap-2.5" data-testid="destination-modal-properties">
                {dest.properties.map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`property-link-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex items-center gap-2 rounded-full border border-[#168A91]/30 bg-[#168A91]/5 px-5 py-2 font-display text-lg italic text-[#13272B] transition-colors duration-300 hover:border-[#168A91] hover:bg-[#168A91] hover:text-[#F7F0E3]"
                  >
                    {p.name}
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <button
            data-testid="destination-modal-enquire"
            onClick={() => onEnquire(dest)}
            className="mt-8 w-full rounded-full bg-[#168A91] py-3.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#116D73]"
          >
            {dest.cta || "Let's Build Your Trip Here"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Destinations = ({ onEnquire }) => {
  const [selected, setSelected] = useState(null);
  const lenis = useLenis();

  useEffect(() => {
    if (selected) lenis?.stop();
    else lenis?.start();
  }, [selected, lenis]);

  const handleEnquire = (dest) => {
    setSelected(null);
    onEnquire(dest.chipLabel || dest.name);
    setTimeout(() => lenis?.scrollTo("#enquiry", { offset: -60, duration: 1.6 }), 60);
  };

  return (
    <section id="destinations" data-testid="destinations-section" className="bg-[#0D1F23] py-24 text-[#F7F0E3] sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-[#E2725B]">
                Curated Inspiration
              </p>
              <h2 className="mt-5 max-w-xl font-display text-3xl font-light leading-tight tracking-tight sm:text-5xl">
                Places that <span className="italic text-[#168A91]">move</span> you
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#F7F0E3]/60 sm:text-base">
              A small, deliberate collection. Each one chosen for what it changes in you, not
              how it looks on a checklist.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          <DestinationCard dest={DESTINATIONS[0]} tall onOpen={setSelected} />
          <DestinationCard dest={DESTINATIONS[1]} offset onOpen={setSelected} />
          <DestinationCard dest={DESTINATIONS[2]} onOpen={setSelected} />
          <DestinationCard dest={DESTINATIONS[4]} tall offset onOpen={setSelected} />
          <div className="lg:col-span-2">
            <DestinationCard dest={DESTINATIONS[3]} onOpen={setSelected} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <DestinationModal dest={selected} onClose={() => setSelected(null)} onEnquire={handleEnquire} />
        )}
      </AnimatePresence>
    </section>
  );
};
